import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getAccessToken,
  storeAccessToken,
  clearTokens,
  authenticatedFetch,
  setDevAuthCookie,
  refreshAccessToken,
  prepareBillingLoginGrant,
  clearBillingLoginToken,
} from '../utils/auth';
import { API_GATEWAY_URL } from '../utils/config';

const AuthContext = createContext(null);

/**
 * AuthProvider component that wraps the app and provides authentication state
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    setIsLoading(true);

    // Check for access token in URL (e.g. post-login redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const accessTokenFromUrl = urlParams.get('accessToken');
    if (accessTokenFromUrl) {
      storeAccessToken(accessTokenFromUrl);
      setDevAuthCookie();
      localStorage.setItem('isAuthenticated', 'true');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check sessionStorage for cached user info first
    const cachedUserInfo = sessionStorage.getItem('userInfo');
    if (cachedUserInfo) {
      try {
        const userInfo = JSON.parse(cachedUserInfo);
        setUser(userInfo);
        setIsAuthenticated(true);
        setIsLoading(false);
        return true;
      } catch {
        sessionStorage.removeItem('userInfo');
      }
    }

    // Check if we have a token, or try to refresh if we don't
    // (refresh token is in HttpOnly cookie, so we can refresh even without access token)
    const token = getAccessToken();
    if (!token) {
      // No access token - try to refresh using the HttpOnly refresh token cookie
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
        return false;
      }
    }

    try {
      const response = await authenticatedFetch(`${API_GATEWAY_URL}/auth/check`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated && data.user) {
          setUser(data.user);
          setIsAuthenticated(true);
          sessionStorage.setItem('userInfo', JSON.stringify(data.user));
          localStorage.setItem('isAuthenticated', 'true');
          setIsLoading(false);
          return true;
        }
      }
    } catch (error) {
      console.log('Not authenticated:', error);
    }

    // Clear invalid tokens
    clearTokens();
    setIsAuthenticated(false);
    setUser(null);
    setIsLoading(false);
    return false;
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      const billingLoginTokenHash = await prepareBillingLoginGrant();
      const response = await fetch(`${API_GATEWAY_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, billingLoginTokenHash }),
      });

      const data = await response.json();

      if (response.ok && data.accessToken) {
        clearBillingLoginToken();
        storeAccessToken(data.accessToken);
        setDevAuthCookie();
        setUser(data.user);
        setIsAuthenticated(true);
        sessionStorage.setItem('userInfo', JSON.stringify(data.user));
        localStorage.setItem('isAuthenticated', 'true');
        return { success: true, user: data.user };
      } else {
        const result = {
          success: false,
          error: data.message || 'Login failed',
          needsVerification: data.needsVerification,
          isDeactivated: data.isDeactivated,
          paymentRequired: data.paymentRequired,
          checkoutUrl: data.checkoutUrl,
          email: data.email
        };
        if (!result.paymentRequired || !result.checkoutUrl) {
          clearBillingLoginToken();
        }
        return result;
      }
    } catch (error) {
      clearBillingLoginToken();
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Register function
  const register = useCallback(async (email, password, displayName, consentOptions = {}) => {
    try {
      const billingLoginTokenHash = await prepareBillingLoginGrant();
      const response = await fetch(`${API_GATEWAY_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          displayName,
          consentUpdates: consentOptions.consentUpdates || false,
          consentPartners: consentOptions.consentPartners || false,
          consentTextVersion: consentOptions.consentTextVersion,
          billingLoginTokenHash,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
          return {
            success: true,
            requiresPayment: true,
            checkoutUrl: data.checkoutUrl,
            ref: data.ref,
            message: data.message,
          };
        }

        // If registration returns an accessToken, the email was pre-verified
        // (blanket verification) and the user is auto-logged in
        if (data.accessToken) {
          clearBillingLoginToken();
          storeAccessToken(data.accessToken);
          setDevAuthCookie();
          if (data.user) {
            setUser(data.user);
            setIsAuthenticated(true);
            sessionStorage.setItem('userInfo', JSON.stringify(data.user));
            localStorage.setItem('isAuthenticated', 'true');
          }
        }
        clearBillingLoginToken();
        return { success: true, message: data.message, requiresVerification: !data.accessToken };
      } else {
        clearBillingLoginToken();
        return { success: false, error: data.message || 'Registration failed' };
      }
    } catch (error) {
      clearBillingLoginToken();
      console.error('Registration error:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    clearTokens();

    try {
      await fetch(`${API_GATEWAY_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    window.location.href = '/';
  }, []);

  // Resend verification email
  const resendVerification = useCallback(async (email) => {
    try {
      const response = await fetch(`${API_GATEWAY_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.message || 'Failed to resend verification email' };
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Verify email token (handles both account and consent-only verification)
  const verifyEmail = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_GATEWAY_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.accessToken && data.user) {
          // Account verification (or consent verification with existing account)
          storeAccessToken(data.accessToken);
          setDevAuthCookie();
          setUser(data.user);
          setIsAuthenticated(true);
          sessionStorage.setItem('userInfo', JSON.stringify(data.user));
          localStorage.setItem('isAuthenticated', 'true');
        }
        // Return user only if auto-logged in; consent-only verification has no user
        return { success: true, message: data.message, user: data.user || null };
      } else {
        return {
          success: false,
          error: data.message || 'Verification failed',
          expired: response.status === 410,
          email: data.email
        };
      }
    } catch (error) {
      console.error('Email verification error:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Verify email change token
  const verifyEmailChange = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_GATEWAY_URL}/auth/verify-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh user data to get updated email
        await checkAuth();
        return { success: true, message: data.message, user: data.user };
      } else {
        return {
          success: false,
          error: data.message || 'Verification failed',
          expired: response.status === 410,
        };
      }
    } catch (error) {
      console.error('Email change verification error:', error);
      return { success: false, error: 'Network error' };
    }
  }, [checkAuth]);

  // Request password reset (forgot password)
  const requestPasswordReset = useCallback(async (email) => {
    try {
      const response = await fetch(`${API_GATEWAY_URL}/auth/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.message || 'Failed to send password reset email' };
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Reset password with token (from email link)
  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const response = await fetch(`${API_GATEWAY_URL}/auth/password-reset`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        // Auto-login after password reset
        if (data.accessToken) {
          storeAccessToken(data.accessToken);
          setDevAuthCookie();
          setUser(data.user);
          setIsAuthenticated(true);
          sessionStorage.setItem('userInfo', JSON.stringify(data.user));
          localStorage.setItem('isAuthenticated', 'true');
        }
        return { success: true, message: data.message, user: data.user };
      } else {
        return {
          success: false,
          error: data.message || 'Password reset failed',
          expired: response.status === 410,
        };
      }
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Change password (authenticated user)
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const response = await authenticatedFetch(`${API_GATEWAY_URL}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update access token if a new one was provided
        if (data.accessToken) {
          storeAccessToken(data.accessToken);
          setDevAuthCookie();
        }
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.message || 'Failed to change password' };
      }
    } catch (error) {
      console.error('Change password error:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    resendVerification,
    verifyEmail,
    verifyEmailChange,
    requestPasswordReset,
    resetPassword,
    changePassword,
    checkAuth,
    authenticatedFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;

